import { apiClient } from '@/lib/api-client'
import { logger } from '@/lib/logger'
import { AUTH_CONFIG } from '@/lib/config'
import { queryClient } from '@/lib/query-client'
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  InviteSignupRequest,
  User,
  Organization,
} from '../types/auth.types'

/**
 * Calculate token expiry in milliseconds from either expires_in (duration) or expires_at (timestamp)
 * Handles both Supabase (expires_at in seconds) and backend (expires_in in seconds) formats
 */
function calculateTokenExpiryMs(expiresIn?: number, expiresAt?: number): number {
  // If we have expires_at (absolute timestamp in seconds), convert to ms
  if (expiresAt && expiresAt > 0) {
    return expiresAt * 1000
  }
  // If we have expires_in (duration in seconds), calculate from now
  if (expiresIn && expiresIn > 0) {
    return Date.now() + expiresIn * 1000
  }
  // Default to configured expiry
  return Date.now() + AUTH_CONFIG.DEFAULT_TOKEN_EXPIRY * 1000
}

/**
 * Authentication Service
 * Handles all auth-related API calls
 */
export const authService = {
  /**
   * Login with email and password
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/api/v1/auth/login', data)

    logger.debug('Auth Service: Login response', {
      hasAccessToken: !!response.access_token,
      hasRefreshToken: !!response.refresh_token,
      expiresIn: response.expires_in,
      expiresAt: response.expires_at,
      tokenType: response.token_type
    })

    // Store tokens in API client - handles both expires_in and expires_at
    const tokenExpiryMs = calculateTokenExpiryMs(response.expires_in, response.expires_at)

    logger.debug('Auth Service: Calculated token expiry', {
      expiresIn: response.expires_in,
      expiresAt: response.expires_at,
      tokenExpiryMs,
      expiresAtDate: new Date(tokenExpiryMs).toISOString()
    })

    apiClient.setTokens(response.access_token, response.refresh_token, tokenExpiryMs)

    return response
  },

  /**
   * Register new organization (admin user)
   */
  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/api/v1/auth/register', data)

    // Store tokens - handles both expires_in and expires_at
    const tokenExpiryMs = calculateTokenExpiryMs(response.expires_in, response.expires_at)
    apiClient.setTokens(response.access_token, response.refresh_token, tokenExpiryMs)

    return response
  },

  /**
   * Register via invitation
   */
  registerWithInvitation: async (data: InviteSignupRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      '/api/v1/auth/register-with-invitation',
      data
    )

    // Store tokens - handles both expires_in and expires_at
    const tokenExpiryMs = calculateTokenExpiryMs(response.expires_in, response.expires_at)
    apiClient.setTokens(response.access_token, response.refresh_token, tokenExpiryMs)

    return response
  },

  /**
   * Get current user profile
   */
  getProfile: async (): Promise<{ user: User; organization: Organization }> => {
    return apiClient.get('/api/v1/auth/me')
  },

  /**
   * Logout (optional - backend may track sessions)
   * Clears all cached data to prevent cross-user data leaks
   */
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/api/v1/auth/logout')
    } catch (error) {
      // Ignore logout errors
      logger.error('Logout error:', error)
    } finally {
      // Always clear local tokens
      apiClient.clearTokens()
      // Clear all React Query cache to prevent cross-organization data leaks
      queryClient.clear()
    }
  },

  /**
   * Change password
   */
  changePassword: async (data: {
    current_password: string
    new_password: string
  }): Promise<void> => {
    return apiClient.post('/api/v1/auth/change-password', data)
  },

  /**
   * Check if user profile exists and has organization
   */
  checkProfile: async (): Promise<{
    exists: boolean
    has_org: boolean
    user: User | null
    organization: Organization | null
  }> => {
    return apiClient.get('/api/v1/auth/check-profile')
  },

  /**
   * Validate beta password for organization creation
   */
  validateBeta: async (password: string): Promise<{ valid: boolean }> => {
    return apiClient.post('/api/v1/auth/validate-beta', { beta_password: password })
  },

  /**
   * Create organization for OAuth user
   */
  createOrg: async (orgName: string, betaPassword: string): Promise<{
    organization: Organization
    user: User
  }> => {
    return apiClient.post('/api/v1/auth/create-org', {
      organization_name: orgName,
      beta_password: betaPassword,
    })
  },
}
